import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'
import { withApiState } from '@/utils/withApiState'

interface Lead {
  uuid: string
  source: string

  contactFirstname: string
  contactLastname: string
  contactEmail: string
  contactPhone: string
  companyName: string
  companySiret: string
  status: string
  createdAt: string
}

interface PaginatedLeads {
  data: Lead[]
  total: number
  page: number
  limit: number
  totalPages: number
  statusCounts?: Record<string, number>
}

interface GetLeadsFilterGridDto {
  // Ajoutez ici les propriétés de filtrage selon votre DTO
  source?: string
  status?: string
  companyName?: string
  contactEmail?: string
  authorUuid?: string
  // etc...
}

interface UpdateLeadDto {
  ownerUuid?: string
  status?: string
  comment?: string
  recipient?: string
  metadata?: Record<string, unknown>
  data?: Record<string, unknown>
  familyCodes?: string[]
  // autres champs si nécessaire
}

interface CreateLeadDto {
  type: string
  source: string
  recipient: string
  contactFirstname: string
  contactLastname: string
  contactEmail?: string
  contactPhone?: string
  companyName: string
  companySiret: string
  data?: Record<string, unknown>
  familyCodes?: string[]
}

export const useLeadStore = defineStore('lead', () => {
  const suiteStore = useSuiteStore()
  const sentLeads = ref<any[]>([])
  const receivedLeads = ref<any[]>([])
  const isLoadingSentLeads = ref(false)
  const isLoadingReceivedLeads = ref(false)
  const isLoadingLeadDetail = ref(false)
  const statusCounts = ref<Record<string, number>>({
    WAITING: 0,
    ACCPETED: 0,
    REFUSED: 0,
    WON: 0,
    LOST: 0,
  })
  const totalSentLeads = ref(0)
  const currentSentPage = ref(1)
  const totalSentPages = ref(0)

  const totalReceivedLeads = ref(0)
  const currentReceivedPage = ref(1)
  const totalReceivedPages = ref(0)

  const fetchLeads = async (
    scope: 'sent' | 'received',
    pageSize: number = 10,
    page: number = 1,
    filters?: GetLeadsFilterGridDto
  ) => {
    const isReceived = scope === 'received'
    if (isReceived) {
      isLoadingReceivedLeads.value = true
    } else {
      isLoadingSentLeads.value = true
    }

    try {
      const response = await suiteStore.suiteFetch('leads/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pageSize,
          page,
          scope,
          filters,
        }),
        credentials: 'include',
      })

      if (response !== false && response.ok) {
        const data: PaginatedLeads = await response.json()

        if (isReceived) {
          receivedLeads.value = data.data
          totalReceivedLeads.value = data.total
          currentReceivedPage.value = data.page
          totalReceivedPages.value = data.totalPages
        } else {
          sentLeads.value = data.data
          totalSentLeads.value = data.total
          currentSentPage.value = data.page
          totalSentPages.value = data.totalPages
        }
        if (data.statusCounts) {
          statusCounts.value = data.statusCounts
        }

        return data
      }

      const bodyText = response === false ? 'Suite unavailable' : await response.text()
      throw new Error(bodyText)
    } catch (error) {
      console.error('Erreur lors de la recherche des leads:', error)
      throw error
    } finally {
      if (isReceived) {
        isLoadingReceivedLeads.value = false
      } else {
        isLoadingSentLeads.value = false
      }
    }
  }

  const fetchSentLeads = (pageSize: number = 10, page: number = 1, filters?: GetLeadsFilterGridDto) =>
    fetchLeads('sent', pageSize, page, filters)

  const fetchReceivedLeads = (pageSize: number = 10, page: number = 1, filters?: GetLeadsFilterGridDto) =>
    fetchLeads('received', pageSize, page, filters)

  const updateLead = async (uuid: string, leadData: UpdateLeadDto) => {
    return withApiState(
      async () => {
        const response = await suiteStore.suiteFetch(`leads/uuid/${uuid}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadData),
          credentials: 'include',
        })

        if (response !== false && response.ok) {
          return await response.json()
        }

        const bodyText = response === false ? 'Suite unavailable' : await response.text()
        throw new Error(bodyText)
      },
      { logLabel: 'Erreur lors de la mise à jour du lead :', rethrow: true },
    )
  }

  const fetchLeadByUuid = async (uuid: string) => {
    return withApiState(
      async () => {
        const response = await suiteStore.suiteFetch(`leads/uuid/${uuid}`, {
          method: 'GET',
          credentials: 'include',
        })

        if (response !== false && response.ok) {
          return await response.json()
        }

        const bodyText = response === false ? 'Suite unavailable' : await response.text()
        throw new Error(bodyText)
      },
      { logLabel: 'Erreur lors du chargement du lead :', rethrow: true },
    )
  }

  const createLead = async (leadData: CreateLeadDto) => {
    return withApiState(
      async () => {
        const response = await suiteStore.suiteFetch('leads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(leadData),
          credentials: 'include',
        })

        if (response !== false && response.ok) {
          return await response.json()
        }

        const bodyText = response === false ? 'Suite unavailable' : await response.text()
        throw new Error(bodyText)
      },
      { logLabel: 'Erreur lors de la création du lead :', rethrow: true },
    )
  }

  return {
    sentLeads,
    receivedLeads,
    isLoadingSentLeads,
    isLoadingReceivedLeads,
    isLoadingLeadDetail,
    statusCounts,
    totalSentLeads,
    currentSentPage,
    totalSentPages,
    totalReceivedLeads,
    currentReceivedPage,
    totalReceivedPages,
    fetchSentLeads,
    fetchReceivedLeads,
    createLead,
    updateLead,
    fetchLeadByUuid,
  }
})
